import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  getLocationsList,
  getProductStateList,
  getProductsList,
  productsByUser,
  transferProduct,
  transportProduct,
  updateProductDB,
  getProductLotList,
} from "../Redux/products/slice";
import { PropertyTableConfig, tableConfig } from "../components/mockData";
import moment from "moment";
import ProductPanel from "./ProductPanel";
import { saveProperties, saveDraftProperties, deleteDraftProperties } from "../Redux/properties/PropertiesSlice";
import { deleteValue } from "../Redux/CounterSlice";
import FormDialog, {
  TAG_TRANSFORM_ACTION,
  TAG_TRANSPORT_ACTION,
} from "./TransportationModal";
import TransportationModal from "./TransportationModal";
import {  createStyles,
  Table,
  ScrollArea,
  UnstyledButton,
  Group,
  Text,
  Center,
  TextInput,
  rem, } from '@mantine/core';
import { IconSelector, IconChevronDown, IconChevronUp, IconSearch } from '@tabler/icons-react';
import Dropdown from "../components/Dropdown";
import CreatePropertiesModal from "../components/CreatePropertiesModal";
import LoadImageModal from "../components/LoadImageModal";
import "react-toastify/dist/ReactToastify.css";
import { Paper,  Container, Grid, Button, Image, Badge, Input, Modal, Loader } from '@mantine/core';
import { useRefresh } from '@mantine/hooks';
import RemoveFieldConfirmationModal from '../components/RemoveFieldConfirmationModal';
import { IntlProvider, FormattedMessage, useIntl } from 'react-intl';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFilePdf } from '@fortawesome/free-solid-svg-icons';
import { BASE_URL } from '../Redux/apiConfig';

const useStyles = createStyles((theme) => ({
  th: {
    padding: '0 !important',
  },
  control: {
    width: '100%',
    padding: `${theme.spacing.xs} ${theme.spacing.md}`,

    '&:hover': {
      backgroundColor: theme.colorScheme === 'dark' ? theme.colors.dark[6] : theme.colors.gray[0],
    },
  },
  icon: {
    width: rem(21),
    height: rem(21),
    borderRadius: rem(21),
  },
  resizeHandle: {
    position: 'absolute',
    top: 0,
    right: 0,
    bottom: 0,
    width: '8px',
    zIndex: 1,
    backgroundColor: '#fff',
  },
  resizeOverlay: {
    position: 'absolute',
    top: 0,
    right: 0,
    bottom: 0,
    width: '8px',
    zIndex: 2,
    opacity: 0,
    cursor: 'col-resize',
  },
  pageBackground: {
    backgroundColor: '#f8f7f3',
    minHeight: '100vh',
    padding: theme.spacing.md, // Increased from xs to md
  },
  
  productCard: {
    backgroundColor: theme.white,
    transition: 'transform 0.2s ease, box-shadow 0.2s ease',
    marginBottom: theme.spacing.xl, // Increased from lg to xl for more space between cards
    '&:hover': {
      transform: 'translateY(-2px)',
      boxShadow: theme.shadows.sm,
    },
    padding: theme.spacing.xl,
  },
  searchFilterCard: {
    backgroundColor: theme.white,
    marginBottom: theme.spacing.xl * 2, // Doubled to create more space
    boxShadow: theme.shadows.xs,
  },
  tableContainer: {
    maxHeight: '200px',
    overflowY: 'auto',
    backgroundColor: theme.white,
  },
  cardContent: {
    padding: theme.spacing.md, // Ensure consistent padding inside cards
  },
  cardSection: {
    marginTop: theme.spacing.md, // Consistent margin between sections
  },
  quickActions: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing.sm, // Increased from xs to sm
    marginBottom: theme.spacing.md, // Increased from sm to md
  },
}));

function Th({ children, reversed, sorted, onSort }) {
  const { classes } = useStyles();
  const Icon = sorted ? (reversed ? IconChevronUp : IconChevronDown) : IconSelector;
  return (
    <th className={classes.th}>
      <UnstyledButton onClick={onSort} className={classes.control}>
        <Group position="apart">
          <div> {/* Wrap the contents of the <th> element */}
            <Text fw={500} fz="sm">
              {children}
            </Text>
            {sorted && <Icon size="0.9rem" stroke={1.5} />} 
          </div>
        </Group>
      </UnstyledButton>
    </th>
  );
}

function filterData(data, search) {
  const query = search.toLowerCase().trim();
  return data.filter((item) =>
    Object.values(item).some((value) => {
      if (typeof value === 'string') {
        return value.toLowerCase().includes(query);
      } else if (typeof value === 'number' || typeof value === 'bigint') {
        return value.toString().includes(query);
      }
      // Handle other data types if needed
      return false; // Return false for non-searchable types
    })
  );
}

function sortData(data, payload) {
  const { sortBy } = payload;

  let sortedData = [...data];

  if (sortBy) {
    sortedData.sort((a, b) => {
      let valueA = a[sortBy];
      let valueB = b[sortBy];

      if (sortBy === 'sku') {
        valueA = parseInt(valueA, 10);
        valueB = parseInt(valueB, 10);
      }

      if (payload.reversed) {
        return valueB - valueA;
      }

      return valueA - valueB;
    });
  }

  return filterData(sortedData, payload.search);
}

const BLOB_IMG = 'https://vertoledgerstorage.blob.core.windows.net/sqldbledgerdigests/images';

function ProductImage({ product }) {
  const png = `${BLOB_IMG}/${product.productID}.png`;
  const jpg = `${BLOB_IMG}/${product.product_name_id}.jpg`;
  const [src, setSrc] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r1 = await fetch(png, { method: 'GET' });
        if (!cancelled) {
          if (r1.ok) { setSrc(png); return; }
          const r2 = await fetch(jpg, { method: 'GET' });
          if (!cancelled) setSrc(r2.ok ? jpg : '');
        }
      } catch { if (!cancelled) setSrc(''); }
    })();
    return () => { cancelled = true; };
  }, [png, jpg]);

  if (src === null) {
    return <div style={{ height: 100, borderRadius: '8px', backgroundColor: '#f0f0f0' }} />;
  }
  if (!src) {
    return (
      <div style={{ height: 100, borderRadius: '8px', backgroundColor: '#f5f5f5', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <span style={{ fontSize: '1.5rem' }}>📦</span>
      </div>
    );
  }
  return (
    <img src={src} alt={product.productName}
      style={{ width: '100%', height: 100, objectFit: 'cover', borderRadius: '8px', display: 'block' }}
    />
  );
}

export default function MobileMyProducts() {
  const dispatch = useDispatch();
  const data = useSelector((state) => state.products.productLotList);
  const locationList = useSelector((state) => state.products.locationsList);
  const productsList = useSelector((state) => state.products.productsList);
  const [show, setShow] = useState(false);
  const [selectedData, setSelectedData] = useState();
  const userinfo = JSON.parse(localStorage.getItem("userinfo"));
  const [openExtrasDialog, setOpenExtrasDialog] = useState(false);
  const [extrasTitle, setExtrasTitle] = useState();
  const [persistedPayload, setPayload] = useState();
  const [tag, setTag] = useState();
  const [selectedAction, setSelectedAction] = useState('');
  const [search, setSearch] = useState('');
  const [sortedData, setSortedData] = useState(data || []);
  useEffect(() => {
    if (data && data.length > 0) {
      setSortedData(data);
    }
  }, [data]);
  const [sortBy, setSortBy] = useState(null);
  const [reverseSortDirection, setReverseSortDirection] = useState(false);
  const [modal, setModal] = useState(false);
  const [loadImageModalVisible, setLoadImageModalVisible] = useState(false);
  const [showSellConfirmationModal, setShowSellConfirmationModal] = useState(false);
  const [sellConfirmationFieldName, setSellConfirmationFieldName] = useState('');
  const selectedLanguage = useSelector((state) => state.language);

  const handleSellButtonClick = (fieldName) => {
    if (fieldName.publish === "N") {
      setSellConfirmationFieldName(fieldName);
      setShowSellConfirmationModal(true);
    } else {
      // Assuming there is a publish method on fieldName, replace it with the actual method
      publish(fieldName); // Call the publish method directly
    }
  };

  const setSorting = (field) => {
    const reversed = field === sortBy ? !reverseSortDirection : false;
    setReverseSortDirection(reversed);
    setSortBy(field);
    const sorted = sortData(data, { sortBy: field, reversed, search });
    setSortedData(sorted);
  };
  
  // const handleSearchChange = (event) => {
  //   const { value } = event.currentTarget;
  //   setSearch(value);
  //   const sorted = sortData(data, { sortBy, reversed: reverseSortDirection, search: value });
  //   setSortedData(sorted);
  // };
  
  const getWithdrawLabel = (field) => {
    return field.publish === "Y" ? <FormattedMessage id="myprods.state.withdraw" /> : <FormattedMessage id="myprods.state.sell" />;
  };

  const handleActionChange = (value, data) => {

    setSelectedAction(value);
    setSelectedData(data);

    switch (value) {
      case "LoadImage":
        setLoadImageModalVisible(true);
        break;
      case "UpdateRelease":
        setSelectedForUpdateRelease(data); // Set selected data for update/release
        setModal(true);
        break;
      case "Delete":
        dispatch(deleteDraftProperties(data));
        break;
      case "Sell":
        handleSellButtonClick(data);
        break;      
      case "View":
        setShow(true);
        break;
      case "Transport":
        transport(data);
        break;
      case "Transform":
        transfer(data);
        break;
      default:
        break;
    }
  };

  const onSave = (obj, cb) => {
    dispatch(saveProperties({ payload: obj, cb }));
  };

  const onDraftSave = (obj, cb) => {
    dispatch(saveDraftProperties({ payload: obj, cb }));
  };
  
  const publish = (data) => {
    var obj = {
      ...data,
      contractId: userinfo.product_contractId,
      upc: data.UPC,
      publish: data.publish === "Y" ? "N" : "Y",
      state: data.state === "ForSale" ? "NotForSale" : "ForSale",
    };
  
    dispatch(updateProductDB(obj));
  };
  
  useEffect(() => {
    dispatch(productsByUser());
    dispatch(getProductsList());
    dispatch(getLocationsList());
    dispatch(getProductLotList());
    dispatch(getProductStateList());
  }, []);

  // useEffect(() => {
  //   dispatch(getProductLotList());
  // }, [dispatch, selectedLanguage]);

  const transfer = (obj) => {
    const product_contractId = JSON.parse(
      localStorage.getItem("userinfo")
    ).product_contractId;

    const locationId = locationList.find(state => state.location === obj.location)?.id || null;
    const id_category = productsList.find(state => state.label === obj.productName)?.id_category || null;

    var payload = {
      contractID: product_contractId,
      sku: obj.sku,
      productLotUuid: obj.productID,
      physicalState: obj.physicalState,
      location: obj.location,
      period: obj.period,
      categoryName: obj.productName,
      locationId: locationId,
      category: id_category,
      product_ref_id: obj.product_ref_id,
    };
    setPayload(payload);
    setExtrasTitle(<FormattedMessage id="myprods.xtratitle.transform" />);
    setOpenExtrasDialog(true);
    setTag(TAG_TRANSFORM_ACTION);
  };

  const transport = (obj) => {
    const product_contractId = JSON.parse(
      localStorage.getItem("userinfo")
    ).product_contractId;

    const locationId = locationList.find(state => state.location === obj.location)?.id || null;

    var payload = {
      contractID: product_contractId,
      sku: obj.sku,
      productLotUuid: obj.productID,
      location: obj.location,
      period: obj.period,
      locationId: locationId,
      product_ref_id: obj.product_ref_id,
    };

    setPayload(payload);
    setExtrasTitle(<FormattedMessage id="myprods.xtratitle.transport" />);
    setOpenExtrasDialog(true);
    setTag(TAG_TRANSPORT_ACTION);
  };

  const classes = useStyles();
  const [selectedForUpdateRelease, setSelectedForUpdateRelease] = useState(null);

  const getActionsForProduct = (product) => {
    if (product.state === "Draft") {
      return [
        {
          value: "UpdateRelease",
          label: <FormattedMessage id="myprods.action.updaterelease" />,
        },{
          value: "Delete",
          label: <FormattedMessage id="myprods.action.delete" />,
        },{
          value: "LoadImage",
          label: <FormattedMessage id="myprods.action.loadimg" />,
        },
      ];
    } else {
      return [
        {
          value: "Sell",
          label: getWithdrawLabel(product),
        },
        { value: "View", label: <FormattedMessage id="myprods.action.view" /> },
        { value: "Transport", label: <FormattedMessage id="myprods.action.transport" /> },
        { value: "Transform", label: <FormattedMessage id="myprods.action.transform" /> },
        {
          value: "LoadImage",
          label: <FormattedMessage id="myprods.action.loadimg" />,
        },
      ];
    }
  };

  const renderMobileProducts = () => {
    return sortedData.map((product, index) => (
      <Paper
        key={index}
        shadow="xs"
        p="md"
        withBorder
        mb="sm"
        className={classes.productCard}
        style={{ borderRadius: '8px' }}
      >
        <Grid>
          {/* Image and main details */}
          <Grid.Col span={4}>
            <ProductImage product={product} />
          </Grid.Col>
          
          <Grid.Col span={8}>
            <div style={{ display: 'flex', flexDirection: 'column', height: '100%', justifyContent: 'space-between' }}>
              <div>
                <Text weight={600} size="lg" mb={4}>{product.productName}</Text>
                <Badge color={product.state === "ForSale" ? "green" : product.state === "Draft" ? "blue" : "gray"} mb={5}>
                  {product.state}
                </Badge>
                {renderQuickActions(product)}
              </div>
              
              <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                <Dropdown
                  actions={getActionsForProduct(product)}
                  handleActionChange={handleActionChange}
                  data={product}
                />
                {product.state !== "Draft" && (
                  <Button 
                    variant="subtle" 
                    color="red"
                    p={5}
                    mx={5}
                    onClick={async () => {
                      try {
                        const productResponse = await fetch(`${BASE_URL}/getProductCertificate?product_id=${product.productID}&location=${encodeURIComponent(product.location)}&targetLanguage=${selectedLanguage}`);
                        if (productResponse.ok) {
                          // Open certificate page using SKU
                          const certificateUrl = `${window.location.origin}/certificate/${product.sku}`;
                          window.open(certificateUrl, '_blank');
                        }
                      } catch (error) {
                        console.error('Error fetching certificate:', error);
                      }
                    }}
                  >
                    <FontAwesomeIcon icon={faFilePdf} color="red" />
                  </Button>
                )}
              </div>
            </div>
          </Grid.Col>
          
          {/* Details section */}
          <Grid.Col span={12}>
            <Grid mt={5}>
              <Grid.Col span={6}>
                <Text size="sm" color="dimmed">SKUID</Text>
                <Text>{product.sku}</Text>
              </Grid.Col>
              <Grid.Col span={6}>
                <Text size="sm" color="dimmed">Quantity</Text>
                <Text>{product.quantity}</Text>
              </Grid.Col>
              <Grid.Col span={12}>
                <Text size="sm" color="dimmed">Location</Text>
                <Text>{product.location}</Text>
              </Grid.Col>
            </Grid>
          </Grid.Col>
        </Grid>
      </Paper>
    ));
  };

  const intl = useIntl();

  const [filterState, setFilterState] = useState('All');
  const stateOptions = ['All', 'ForSale', 'NotForSale', 'Draft'];

  // Add this new filter function
  const filterByState = (data, stateFilter) => {
    if (stateFilter === 'All') return data;
    return data.filter(item => item.state === stateFilter);
  };

  // Update your handleSearchChange function
  const handleSearchChange = (event) => {
    const { value } = event.currentTarget;
    setSearch(value);
    let sorted = sortData(data, { sortBy, reversed: reverseSortDirection, search: value });
    sorted = filterByState(sorted, filterState);
    setSortedData(sorted);
  };

  // Add this new function to handle state filter changes
  const handleStateFilterChange = (value) => {
    setFilterState(value);
    let sorted = sortData(data, { sortBy, reversed: reverseSortDirection, search });
    sorted = filterByState(sorted, value);
    setSortedData(sorted);
  };

  const renderSearchAndFilter = () => {
    return (
      <Paper className={classes.searchFilterCard} withBorder p="md" style={{ marginBottom: '1rem' }}>
        <Grid>
          <Grid.Col span={12}>
            <TextInput
              placeholder={intl.formatMessage({ id:"myprods.search.placeholder" })}
              value={search}
              onChange={handleSearchChange}
              icon={<IconSearch size={16} />}
            />
          </Grid.Col>
          <Grid.Col span={12}>
            <Text size="sm" mb={10}>
              <FormattedMessage id="myprods.filter.state" defaultMessage="Filter by State" />
            </Text>
            <div style={{ 
              display: 'flex', 
              gap: '8px', 
              flexWrap: 'wrap', 
              justifyContent: 'center' // Center the filter buttons
            }}>
              {stateOptions.map((state) => (
                <Button
                  key={state}
                  variant={filterState === state ? "filled" : "outline"}
                  size="xs"
                  onClick={() => handleStateFilterChange(state)}
                >
                  {state}
                </Button>
              ))}
            </div>
          </Grid.Col>
        </Grid>
      </Paper>
    );
  };

  const renderQuickActions = (product) => {
    const containerStyle = {
      display: 'flex', 
      flexWrap: 'wrap', 
      gap: '8px', 
      marginTop: '10px', 
      marginBottom: '10px',
      width: '100%'
    };
  
    const buttonStyle = {
      flexGrow: 1,
      flexBasis: 'calc(50% - 8px)', // Two buttons per row with gap consideration
      minWidth: '120px', // Minimum width to prevent too narrow buttons
      maxWidth: '100%', // Ensure buttons don't overflow
    };
  
    // Different quick actions depending on state
    if (product.state === "Draft") {
      return (
        <div style={containerStyle}> 
          <Button 
            size="xs" 
            variant="outline" 
            color="blue"
            style={buttonStyle}
            onClick={() => handleActionChange("UpdateRelease", product)}
          >
            <FormattedMessage 
              id="myprods.action.updaterelease" 
              defaultMessage="Update/Release" 
            />
          </Button>
          <Button 
            size="xs" 
            variant="outline" 
            color="pink"
            style={buttonStyle}
            onClick={() => handleActionChange("LoadImage", product)}
          >
            <FormattedMessage 
              id="myprods.action.loadimg" 
              defaultMessage="Load Image" 
            />
          </Button>
        </div>
      );
    } else {
      return (
        <div style={containerStyle}>
          <Button 
            size="xs" 
            variant="outline" 
            color={product.publish === "Y" ? "red" : "green"}
            style={buttonStyle}
            onClick={() => handleActionChange("Sell", product)}
          >
            {getWithdrawLabel(product)}
          </Button>
          <Button 
            size="xs" 
            variant="outline" 
            color="blue"
            style={buttonStyle}
            onClick={() => handleActionChange("View", product)}
          >
            <FormattedMessage 
              id="myprods.action.view" 
              defaultMessage="View" 
            />
          </Button>
          <Button 
            size="xs" 
            variant="outline"
            style={buttonStyle}
            onClick={() => handleActionChange("Transport", product)}
          >
            <FormattedMessage 
              id="myprods.action.transport" 
              defaultMessage="Transport" 
            />
          </Button>
        </div>
      );
    }
  };

  const [isLoading, setIsLoading] = useState(false);

  const fetchProductData = () => {
    setIsLoading(true);
    
    // Dispatch actions without expecting Promise returns
    dispatch(productsByUser());
    dispatch(getProductsList());
    dispatch(getLocationsList());
    dispatch(getProductLotList());
    dispatch(getProductStateList());
    
    // Use setTimeout to give Redux actions time to complete
    setTimeout(() => {
      setIsLoading(false);
    }, 1000);
  };
  
  // Replace your useEffect for fetching product list
  useEffect(() => {
    fetchProductData();
  }, [dispatch, selectedLanguage]);

  const renderContentState = () => {
    if (isLoading) {
      return (
        <Center style={{ padding: '40px 0' }}>
          <div style={{ textAlign: 'center' }}>
            <Loader size="md" />
            <Text mt="md" color="dimmed">
              <FormattedMessage id="myprods.loading" defaultMessage="Loading products..." />
            </Text>
          </div>
        </Center>
      );
    }
    
    if (!sortedData || sortedData.length === 0) {
      return (
        <Paper p="xl" withBorder style={{ textAlign: 'center' }}>
          <Text size="lg" weight={500} mb="md">
            <FormattedMessage id="myprods.empty.title" defaultMessage="No products found" />
          </Text>
          <Text color="dimmed" mb="xl">
            <FormattedMessage id="myprods.empty.description" defaultMessage="There are no products matching your search criteria" />
          </Text>
          <Button onClick={fetchProductData}>
            <FormattedMessage id="myprods.refresh" defaultMessage="Refresh" />
          </Button>
        </Paper>
      );
    }
    
    return renderMobileProducts();
  };

  return (
    <>
      <TransportationModal
        modal={openExtrasDialog}
        toggle={() => setOpenExtrasDialog(false)}
        title={extrasTitle}
        data={persistedPayload}
        tag={tag}
        onSave={() => {
          // Handle any post-save logic here
          dispatch(getProductLotList()); // Refresh product list after saving
          setOpenExtrasDialog(false); // Close the modal after saving
        }}
      />
      {selectedData && (
        <ProductPanel
          modal={show}
          toggle={() => setShow(false)}
          readonly={true}
          value={selectedData}
        />
      )}
      <ScrollArea  className={classes.pageBackground}>
      {renderSearchAndFilter()}
      {renderContentState()}
        {/* <TextInput
          placeholder={intl.formatMessage({ id:"myprods.search.placeholder" })}
          mb="md"
          onChange={handleSearchChange}
          style={{marginTop: '10px', display: 'flex' }}
        /> */}
        {/* {sortedData && sortedData.length > 0 ? renderMobileProducts() : <p>No products available.</p>} */}
        <CreatePropertiesModal
          modal={modal}
          toggle={() => setModal(false)}
          selectedData={selectedForUpdateRelease}
          onSave={(obj, cb) => onSave(obj, cb)}
          onDraftSave={(obj, cb) => onDraftSave(obj, cb)}
          style={{
            maxHeight: 'calc(100vh - 100px)', // Adjust the height based on your layout and content
            overflowY: 'auto',
          }}
        />
        <LoadImageModal
          modal={loadImageModalVisible}
          toggle={() => setLoadImageModalVisible(!loadImageModalVisible)}
          selectedData={selectedData}
          onSave={(obj, toggle) => {
            // Handle saving logic here
            setLoadImageModalVisible(false); // Close the modal after saving
          }}
          style={{
            maxHeight: 'calc(100vh - 100px)', // Adjust the height based on your layout and content
            overflowY: 'auto',
          }}
        />
        {/* Additional components or modals */}
      </ScrollArea>
      <RemoveFieldConfirmationModal
        fieldName={sellConfirmationFieldName}
        onConfirm={() => {
          // Handle the sell confirmation logic here
          // For example, you can call the sell function or dispatch an action
          publish(sellConfirmationFieldName); 
          setShowSellConfirmationModal(false); // Close the modal after confirming
        }}
        onCancel={() => setShowSellConfirmationModal(false)}
        opened={showSellConfirmationModal}
      />
    </>
  );
}
